import { useState, useEffect } from 'react';
import { SaudiRiyal } from 'lucide-react';

const TicketEditModal = ({ ticket, isOpen, onClose, onSave }) => {
  const [ticketData, setTicketData] = useState({
    id: '',
    name: '',
    price: 0,
    quantity: 0,
    custom_text: '',
    badge_image: null,
    badge_design: {}
  });

  const [badgeImagePreview, setBadgeImagePreview] = useState(null);
  const [isImageChanged, setIsImageChanged] = useState(false);

  // Initialize/reset form when ticket or isOpen changes
  useEffect(() => {
    if (isOpen && ticket) {
      setTicketData({
        id: ticket.id,
        name: ticket.name || '',
        price: ticket.price || 0,
        quantity: ticket.quantity || 1,
        custom_text: ticket.custom_text || '',
        badge_image: null, // Store the file object later if changed
        badge_design: ticket.badge_design || {}
      });
      
      // Set the badge image preview from existing URL
      if (ticket.badge_image && typeof ticket.badge_image === 'string') {
        setBadgeImagePreview(ticket.badge_image);
      } else {
        setBadgeImagePreview(null);
      }
      
      // Reset the image changed flag
      setIsImageChanged(false);
    }
  }, [ticket, isOpen]);

  const handleChange = (e) => {
    const { name, value, type, files } = e.target;

    if (type === 'file') {
      if (files[0]) {
        // Create a preview URL for displaying the image
        const newBadgeURL = URL.createObjectURL(files[0]);
        setBadgeImagePreview(newBadgeURL);
        
        // Store the file object for the API call
        setTicketData(prev => ({ ...prev, [name]: files[0] }));
        
        // Set flag that image has been changed
        setIsImageChanged(true);
      }
    } else {
      setTicketData(prev => ({
        ...prev,
        [name]: name === 'price' || name === 'quantity' ? Number(value) : value
      }));
    }
  };

  // Clean up any object URLs to prevent memory leaks
  useEffect(() => {
    return () => {
      if (badgeImagePreview && isImageChanged) {
        URL.revokeObjectURL(badgeImagePreview);
      }
    };
  }, [badgeImagePreview, isImageChanged]);

  const handleSave = async () => {
    // Create FormData for the API call
    const formData = new FormData();
    
    // Add all ticket data except badge_image
    Object.keys(ticketData).forEach(key => {
      if (key !== 'badge_image' && key !== 'badge_design') {
        formData.append(key, ticketData[key]);
      }
    });
    
    // Add badge_design as a JSON string
    if (Object.keys(ticketData.badge_design).length > 0) {
      formData.append('badge_design', JSON.stringify(ticketData.badge_design));
    }
    
    // Only append the file if it was changed
    if (isImageChanged && ticketData.badge_image) {
      formData.append('badge_image', ticketData.badge_image);
    }
    
    // Add a flag to indicate if the image was changed or not
    formData.append('image_changed', isImageChanged);
    
    // Pass the FormData to the parent component for API handling
    onSave(formData, ticket.id);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-2xl w-full">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-bold">Edit Ticket: {ticket.name}</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <span className="text-2xl">&times;</span>
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">Ticket Name</label>
            <input
              type="text"
              name="name"
              value={ticketData.name}
              onChange={handleChange}
              className="mt-1 block w-full rounded-md border p-2 border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
              placeholder="e.g., Standard Admission, VIP Pass"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Price</label>
            <div className="mt-1 relative rounded-md shadow-sm">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <SaudiRiyal className="h-3.5 w-3.5 text-gray-500" aria-label="SAR" />
              </div>
              <input
                type="number"
                name="price"
                value={ticketData.price}
                onChange={handleChange}
                className="block w-full pl-7 pr-12 rounded-md border p-2 border-gray-300 focus:border-indigo-500 focus:ring-indigo-500"
                placeholder="0.00"
                step="0.01"
                min="0"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Quantity Available</label>
            <input
              type="number"
              name="quantity"
              value={ticketData.quantity}
              onChange={handleChange}
              className="mt-1 block w-full rounded-md border p-2 border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
              min="1"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Custom Text</label>
            <textarea
              name="custom_text"
              value={ticketData.custom_text}
              onChange={handleChange}
              rows="3"
              className="mt-1 block w-full rounded-md border p-2 border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
              placeholder="Additional text to display on the badge"
            ></textarea>
          </div>

          <div>
            <label htmlFor="badge_image" className="block text-sm font-medium text-gray-700">Badge Image</label>
            <input
              type="file"
              id="badge_image"
              name="badge_image"
              onChange={handleChange}
              className="mt-1 block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100"
              accept="image/*"
            />
            {badgeImagePreview && (
              <div className="mt-2">
                <img
                  src={badgeImagePreview}
                  alt="Badge preview"
                  className="h-20 w-auto object-contain"
                />
              </div>
            )}
          </div>
        </div>

        <div className="flex justify-end mt-6 space-x-3">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            Save Changes
          </button>
        </div>
      </div>
    </div>
  );
};

export default TicketEditModal;